import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { useLang } from "@/context/LanguageContext";
import { Bell, Receipt, HelpCircle, MessageSquare, Utensils, Check, Clock } from "lucide-react";
import { toast } from "sonner";

const TYPE_META_STATIC = {
  waiter: { icon: Bell, accent: "#007aff" },
  bill: { icon: Receipt, accent: "#ff3b30" },
  assistance: { icon: HelpCircle, accent: "#ff3b30" },
  order: { icon: Utensils, accent: "#32d74b" },
  other: { icon: MessageSquare, accent: "#a1a1aa" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export default function StaffDashboard() {
  const { t } = useLang();
  const TYPE_META = {
    waiter: { icon: Bell, label: t("waiter_requested"), accent: "#007aff" },
    bill: { icon: Receipt, label: t("bill_requested"), accent: "#ff3b30" },
    assistance: { icon: HelpCircle, label: t("assistance_requested"), accent: "#ff3b30" },
    order: { icon: Utensils, label: t("new_order"), accent: "#32d74b" },
    other: { icon: MessageSquare, label: t("other_request"), accent: "#a1a1aa" },
  };
  const [pending, setPending] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [stats, setStats] = useState(null);
  const [seenIds, setSeenIds] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const [p, c, s] = await Promise.all([
        api.get("/requests?status=pending"),
        api.get("/requests?status=completed"),
        api.get("/stats/overview"),
      ]);

      // Detect new requests to toast
      setSeenIds((prev) => {
        if (prev.size > 0) {
          for (const r of p.data) {
            if (!prev.has(r.id)) {
              const meta = TYPE_META[r.type] || TYPE_META.other;
              toast(`${t("table")} ${r.table_number} — ${meta.label}`, { description: r.note });
            }
          }
        }
        return new Set(p.data.map((r) => r.id));
      });

      setPending(p.data);
      setCompleted(c.data.slice(0, 15));
      setStats(s.data);
    } catch (e) {
      // ignore transient errors
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);

  const markDone = async (id) => {
    try {
      await api.patch(`/requests/${id}/complete`);
      toast.success(t("done"));
      load();
    } catch {
      toast.error(t("could_not_update") || "Could not update");
    }
  };

  return (
    <StaffLayout>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline text-zinc-500">{t("live_feed")}</div>
          <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("requests_title")}</h1>
        </div>
        <div className="text-xs text-zinc-500 font-plex flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#32d74b] animate-pulse" /> {t("auto_refresh")}
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#262626] border border-[#262626]" data-testid="stats-strip">
          <Stat label={t("pending")} value={stats.pending_requests} accent="#ff3b30" />
          <Stat label={t("stat_orders_today")} value={stats.orders_today} />
          <Stat label={t("stat_revenue_today")} value={`€${stats.revenue_today.toFixed(2)}`} accent="#32d74b" />
          <Stat label={t("stat_tables")} value={stats.tables} />
          <Stat label={t("stat_menu_items")} value={stats.menu_items} />
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2">
          <div className="overline text-zinc-500">{t("pending")} · {pending.length}</div>
          <div className="mt-4 space-y-3">
            {pending.length === 0 && (
              <div className="border border-[#262626] p-8 text-center text-zinc-500 font-plex text-sm">
                {t("nothing_pending")}
              </div>
            )}
            {pending.map((r) => {
              const meta = TYPE_META[r.type] || TYPE_META.other;
              const Icon = meta.icon;
              const urgent = r.type === "bill" || r.type === "assistance";
              return (
                <div
                  key={r.id}
                  data-testid={`request-row-${r.id}`}
                  className={`bg-[#121212] border border-[#262626] border-l-4 p-4 flex items-center gap-5 ${urgent ? "urgent-border" : ""}`}
                  style={{ borderLeftColor: meta.accent }}
                >
                  <div className="flex items-center gap-3 min-w-[140px]">
                    <Icon size={18} style={{ color: meta.accent }} />
                    <div>
                      <div className="overline text-zinc-500">{t("table")}</div>
                      <div className="font-condensed font-bold text-2xl leading-none">
                        {String(r.table_number).padStart(2, "0")}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-plex text-sm text-white">{meta.label}</div>
                    {r.note ? <div className="font-plex text-xs text-zinc-400 mt-1 truncate">{r.note}</div> : null}
                  </div>
                  <div className="text-xs text-zinc-500 font-plex flex items-center gap-1.5">
                    <Clock size={12} /> {timeAgo(r.created_at)}
                  </div>
                  <button
                    onClick={() => markDone(r.id)}
                    data-testid={`mark-complete-btn-${r.id}`}
                    className="border border-[#262626] px-3 py-2 font-plex text-xs hover:bg-[#32d74b] hover:text-black hover:border-[#32d74b] transition-colors flex items-center gap-1.5"
                  >
                    <Check size={14} /> {t("done")}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="overline text-zinc-500">{t("recently_cleared")}</div>
          <div className="mt-4 border border-[#262626]">
            {completed.length === 0 && (
              <div className="p-6 text-zinc-500 font-plex text-sm text-center">{t("no_history")}</div>
            )}
            {completed.map((r) => {
              const meta = TYPE_META[r.type] || TYPE_META.other;
              return (
                <div key={r.id} className="px-4 py-3 border-b border-[#262626] last:border-0 flex items-center gap-3">
                  <span className="w-1.5 h-1.5" style={{ background: meta.accent }} />
                  <div className="text-xs text-zinc-500 font-plex w-14">T{r.table_number}</div>
                  <div className="flex-1 font-plex text-xs text-zinc-300 truncate">{meta.label}</div>
                  <div className="text-[10px] text-zinc-600 font-plex">{timeAgo(r.completed_at)}</div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </StaffLayout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#0a0a0a] px-5 py-5">
      <div className="overline text-zinc-500">{label}</div>
      <div className="font-condensed font-bold text-3xl mt-1 tabular-nums" style={accent ? { color: accent } : {}}>
        {value}
      </div>
    </div>
  );
}
